import React, { useCallback } from 'react';

import Skeleton from 'react-loading-skeleton';

import { cn } from '../../lib/utils';

import useResolvedProfile from '../../hooks/useResolvedProfile';
import ProfilePopoverContent from '../ProfilePopoverContent';
import EntityPopover from '../shared/EntityPopover';
import Flexicon from '../shared/Flexicon';

const MessageAvatar = ({ message, author, interactive = true, isPlaceholder = false }) => {
  const isProfileAuthor = message.author_type === 'profile';
  const resolvedAuthor = useResolvedProfile(author, interactive && isProfileAuthor);

  // Clicking an avatar shows the profile popover (via EntityPopover's clickEnabled) — never a DM
  // or account settings — so swallow the click before it reaches an interactive ancestor.
  const handleAvatarClick = useCallback((event) => {
    event?.stopPropagation();
  }, []);

  const popoverContent = isProfileAuthor && resolvedAuthor?.id ? <ProfilePopoverContent profile={resolvedAuthor} /> : null;

  if (isPlaceholder) {
    return (
      <div className="absolute bottom-0 left-0 size-[38px]">
        <Skeleton count={1} width={38} height={38} circle />
      </div>
    );
  }

  return (
    <div className="absolute bottom-px left-0 size-[38px]">
      {isProfileAuthor ? (
        <EntityPopover content={popoverContent} interactive={interactive} clickEnabled={interactive}>
          <button
            tabIndex={interactive ? null : -1}
            onClick={handleAvatarClick}
            aria-label={`View profile for ${resolvedAuthor?.full_name || 'profile'}`}
            data-app-region={interactive ? 'none' : 'drag'}
            className={cn('inline-flex size-[38px] aspect-square', interactive && 'cursor-pointer')}
          >
            <Flexicon resource={resolvedAuthor} size="size-[38px]" />
          </button>
        </EntityPopover>
      ) : (
        <div>
          <Flexicon resource={author} size="size-[38px]" />
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 rounded-full border-[0.5px] border-black/10 dark:hidden" />
    </div>
  );
};

export default MessageAvatar;
